/**
 * Organization access model shared by the server (which enforces it) and the web app (which uses it to
 * decide what to show). Every member can view the organization's projects and diagrams; everything else
 * is an explicit permission. Roles are presets of permissions, and an owner can still tick individual
 * permissions for a member, which turns the member's role into "custom".
 */

export const PERMISSIONS = [
  'diagrams.edit',
  'diagrams.create',
  'diagrams.delete',
  'projects.manage',
  'members.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSION_LABELS: Record<Permission, { label: string; description: string }> = {
  'diagrams.edit': { label: 'Edit diagrams', description: 'Change the content of ER and architecture diagrams' },
  'diagrams.create': { label: 'Create diagrams', description: 'Add new diagrams to a project' },
  'diagrams.delete': { label: 'Delete diagrams', description: 'Remove diagrams from a project' },
  'projects.manage': { label: 'Manage projects', description: 'Create, rename and delete projects' },
  'members.manage': { label: 'Manage members', description: 'Add, remove and change the permissions of members' },
};

export type MemberRole = 'owner' | 'admin' | 'editor' | 'viewer' | 'custom';
export type AssignableRole = Exclude<MemberRole, 'owner'>;

export const ROLE_PRESETS: Record<Exclude<AssignableRole, 'custom'>, Permission[]> = {
  viewer: [],
  editor: ['diagrams.edit', 'diagrams.create'],
  admin: ['diagrams.edit', 'diagrams.create', 'diagrams.delete', 'projects.manage', 'members.manage'],
};

export const ROLE_LABELS: Record<MemberRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
  custom: 'Custom',
};

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

export function normalizePermissions(values: unknown): Permission[] {
  if (!Array.isArray(values)) return [];
  return PERMISSIONS.filter((p) => values.includes(p));
}

/** The role name that best describes a permission set: a preset when it matches one exactly, otherwise "custom". */
export function roleForPermissions(permissions: Permission[]): AssignableRole {
  const sorted = [...permissions].sort().join(',');
  for (const [role, preset] of Object.entries(ROLE_PRESETS)) {
    if ([...preset].sort().join(',') === sorted) return role as AssignableRole;
  }
  return 'custom';
}

export interface MemberAccess {
  role: MemberRole;
  permissions: Permission[];
}

/** Owners implicitly hold every permission, regardless of what is stored for them. */
export function hasPermission(access: MemberAccess | null | undefined, permission: Permission): boolean {
  if (!access) return false;
  return access.role === 'owner' || access.permissions.includes(permission);
}

export const USERNAME_PATTERN = /^[a-zA-Z0-9_.-]{3,32}$/;
export const MIN_PASSWORD_LENGTH = 8;
